/**
 * upmix-render.js — window.__UpmixRender
 *
 * The Web-Audio upmix render that an ffmpeg `pan`/filter graph cannot
 * express: the SPECTRAL method (per-bin phantom centre + ambient rear) for the
 * disc builder, 5.1 only.
 *
 * The engine, the spectral space and the 5.1 rows are NOT here: they are
 * public/js/spectral-upmix.js (window.__SpectralUpmix), the same object
 * /channel-converter renders with. Until 2026-09-23 this file carried a
 * "deliberate copy" of that engine with a browser drift guard nobody ran; the
 * copy missed the 2026-08-29 rewrite of the rear (anti-phase Hafler matrix →
 * extracted ambience) and every spectral disc shipped the old phasey
 * surrounds for a month (measured on the old matrix: SL/SR correlation −0.95,
 * a hard-left source −3.2 dB in the rear). Only the render and the WAV writer
 * are this file's.
 *
 *   const file51 = await window.__UpmixRender.spectral51(stereoFile);   // → 5.1 WAV File
 */
(function (w) {
    'use strict';
    if (w.__UpmixRender) return;

    var SR = 48000;   // pin the render to the DVD/AC-3 target rate

    // The filter shape of a row's lp/hp belongs to the law (upmix-laws.js, BIQUAD_Q_DB).
    // Should that script ever be an older copy than this one, the definition is
    // restated rather than left to the node's default — which is the resonance
    // the number exists to avoid — or to a NaN that would abort the render.
    function lawQ() {
        var q = w.__UpmixLaws && w.__UpmixLaws.BIQUAD_Q_DB;
        return (typeof q === 'number' && isFinite(q)) ? q : 20 * Math.log10(0.707);
    }

    function normChan(c) { return Array.isArray(c) ? { taps: c, lp: 0, hp: 0, dly: 0 } : { taps: c.taps, lp: c.lp || 0, hp: c.hp || 0, dly: c.dly || 0 }; }

    // Render the intermediate through the rows (the same Web-Audio graph as
    // /channel-converter's renderMatrix: splitter → tap gains → lp/hp/delay → merger).
    async function renderMatrix(srcBuf, rows) {
        var chans = rows.map(normChan);
        var inCh = srcBuf.numberOfChannels, sr = srcBuf.sampleRate;
        // The SOURCE's length: tracks laid back to back on a disc must not grow
        // a gap in the fronts at every boundary (2026-10-09 — the same rule as
        // /channel-converter's renderMatrix and UpmixModule).
        var off = new OfflineAudioContext(chans.length, srcBuf.length, sr);
        var src = off.createBufferSource(); src.buffer = srcBuf;
        var splitter = off.createChannelSplitter(inCh); src.connect(splitter);
        var merger = off.createChannelMerger(chans.length); merger.connect(off.destination);
        chans.forEach(function (ch, oc) {
            var bus = off.createGain(); bus.gain.value = 1;
            ch.taps.forEach(function (t) {
                var idx = Math.min(t[0], inCh - 1);
                var g = off.createGain(); g.gain.value = t[1]; splitter.connect(g, idx); g.connect(bus);
            });
            var node = bus;
            // Q from the law (FFmpeg's default shape, in Web Audio's unit) — see BIQUAD_Q_DB in upmix-laws.js.
            if (ch.lp) { var lp = off.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = ch.lp; lp.Q.value = lawQ(); node.connect(lp); node = lp; }
            if (ch.hp) { var h = off.createBiquadFilter(); h.type = 'highpass'; h.frequency.value = ch.hp; h.Q.value = lawQ(); node.connect(h); node = h; }
            if (ch.dly) { var d = off.createDelay(1); d.delayTime.value = ch.dly / 1000; node.connect(d); node = d; }
            node.connect(merger, 0, oc);
        });
        src.start(0);
        return await off.startRendering();
    }

    // 24-bit PCM WAV; the header is the site's one builder (OutputSink.wavHeader:
    // EXTENSIBLE with the 5.1 mask 0x60F above stereo — the old 0x3F here was
    // refused by Chromium ≤154's decodeAudioData).
    function toWav(buf) {
        var nCh = buf.numberOfChannels, sr = buf.sampleRate, n = buf.length, bytes = 3;
        var dataLen = n * nCh * bytes, hdr = w.OutputSink.wavHeader(nCh, sr, 24, dataLen);
        var ab = new ArrayBuffer(hdr.length + dataLen);
        new Uint8Array(ab).set(hdr, 0);
        var v = new DataView(ab), o = hdr.length;
        var chans = []; for (var c = 0; c < nCh; c++) chans.push(buf.getChannelData(c));
        for (var i = 0; i < n; i++) for (var ch = 0; ch < nCh; ch++) {
            var x = chans[ch][i]; if (x > 1) x = 1; else if (x < -1) x = -1;
            var q = Math.max(-0x800000, Math.min(0x7FFFFF, Math.round(x * 0x7FFFFF))); if (q < 0) q += 0x1000000;
            v.setUint8(o++, q & 0xFF); v.setUint8(o++, (q >> 8) & 0xFF); v.setUint8(o++, (q >> 16) & 0xFF);
        }
        return new Blob([ab], { type: 'audio/wav' });
    }

    async function decode(file) {
        var ab = await file.arrayBuffer();
        var oc = new OfflineAudioContext(2, 1, SR);   // decodeAudioData resamples to SR
        return await oc.decodeAudioData(ab);
    }

    /* Stereo File → 5.1 WAV File via the spectral upmix (shared engine). */
    async function spectral51(file) {
        var SU = w.__SpectralUpmix;
        if (!SU) throw new Error('spectral_engine_missing');
        var buf = await decode(file);
        if (buf.numberOfChannels < 2) throw new Error('spectral_needs_stereo');
        var N = buf.length, sr = buf.sampleRate;
        var L = buf.getChannelData(0), R = buf.getChannelData(1);
        var core = await SU.extract(L, R, sr, {});
        var inter = new OfflineAudioContext(6, N, sr).createBuffer(6, N, sr);
        SU.assemble(core, L, R, sr, 1, [inter.getChannelData(0), inter.getChannelData(1), inter.getChannelData(2), inter.getChannelData(3)]);
        inter.copyToChannel(core.outAL, 4); inter.copyToChannel(core.outAR, 5);
        var out = await renderMatrix(inter, SU.rowsFor('5.1', core.ambGain));
        return new File([toWav(out)], 'spectral51.wav', { type: 'audio/wav' });
    }

    w.__UpmixRender = { spectral51: spectral51 };
})(window);
